/**
 * Client-performance lifecycle — the ONE tracker instance for a page load, plus
 * every decision instrumentation needs so bootstrap call sites stay one-liners
 * (task 6 of the client-side-performance plan).
 *
 * Invariants this module owns:
 *  - single attempt: the tracker is created once, LAZILY on first use (never at
 *    module evaluation — a throw there would take the bundle down), from a
 *    defensively-read `window.cpcClientPerformanceMarker`;
 *  - AT MOST ONE report per page attempt — after the first terminal call every
 *    further complete* is a no-op;
 *  - telemetry can never throw into bootstrap, and never blocks it: the report
 *    is fire-and-forget (never awaited) and its outcome only updates the local
 *    `window.cpc.clientPerformance` delivery diagnostic;
 *  - no reporting at all when shop or the API base URL is missing;
 *  - a page whose calculator LOOKUP SUCCEEDED but yielded no calculator is
 *    LATCHED AND NOT SENT (2026-09-11). It was the largest single source of
 *    ingest traffic — 2,104,507 rows in 24 hours against 164,936 attempts that
 *    measured anything — and all it ever said was "this store has a calculator
 *    assigned to no products", which the database already knows and can answer
 *    without waiting for a shopper, including for stores with no traffic at
 *    all. The reason stays visible in `window.cpc.clientPerformance`. The
 *    server still accepts and counts the result, because cached bundles keep
 *    sending it for days after this ships.
 *    A pageurl lookup that never got an answer is not an outcome at all
 *    (DIV-022): DetectPage carries on and the page view reports whatever
 *    happens next.
 *  - BENIGN lookup failures are SUPPRESSED, not reported. In the first 25 min
 *    after the 2026-09-06 release, 62% of all attempts were
 *    `configuration_request_failed` with reason `TypeError`, carrying
 *    `initialization_started` but NOT `loader_started` — i.e. the very first
 *    pageurl lookup in DetectPage rejecting. Those are overwhelmingly shoppers
 *    leaving the page mid-request (iOS back/forward, slow mobile), offline
 *    devices and client-side blockers, not real API/CORS faults. So a
 *    `configuration_request_failed` raised while the page is hidden/unloading
 *    or the device is offline LATCHES the attempt without sending, and records
 *    why in the debug-only diagnostics. Every other result (runtime_error,
 *    invalid_configuration, mount_timeout) reports exactly as before, hidden or
 *    not — those are real defects wherever they happen.
 *
 * Imports are limited to the tracker, the reporter and appState — never a
 * bootstrap module — so instrumenting bootstrap cannot create an import cycle.
 */
import { appState } from "../state/app-state";
import { payloadSource } from "../api/calculator-resolve";
import {
  createClientPerformanceTracker,
  type ClientPerformanceTracker,
  type ClientPerformanceAttemptPayload,
  type ClientPerformanceMilestone,
  type ClientPerformanceFailureResult,
} from "./client-performance-tracker";
import {
  reportClientPerformanceAttempt,
  resolveClientPerformanceEndpoint,
  type ClientPerformanceDeliveryOutcome,
} from "./client-performance-reporter";

interface Identity {
  calculatorId?: string;
  calculatorName?: string;
  calculatorVersion?: string;
}

/**
 * The theme app embed may stamp `window.cpcClientPerformanceMarker` before the
 * bundle parses. It is merchant-controlled markup, so treat it as hostile:
 * only finite non-negative timestamps and a known `runtimePath` survive.
 *
 * Besides `embedSeenAtMs`, the embed records three moments of ITS OWN loading
 * (#116 item 19): `bootAtMs` (its idle wait ended), `jqueryReadyAtMs` (jQuery
 * present, loaded or already there) and `bundleRequestedAtMs` (our <script>
 * tag appended). With `initialization_started` they split the biggest phase of
 * a shopper's wait — embed seen → bundle running — into idle wait, jQuery,
 * and bundle download+parse. Embeds older than that stamp none of them.
 */
type EmbedMarker = {
  embedSeenAtMs?: number;
  bootAtMs?: number;
  jqueryReadyAtMs?: number;
  bundleRequestedAtMs?: number;
  runtimePath?: "legacy" | "v3";
};
const MARKER_TIMESTAMPS = ["embedSeenAtMs", "bootAtMs", "jqueryReadyAtMs", "bundleRequestedAtMs"] as const;

function readMarker(): EmbedMarker | undefined {
  try {
    const raw = window.cpcClientPerformanceMarker as Record<string, unknown> | undefined;
    if (!raw || typeof raw !== "object") return undefined;
    const marker: EmbedMarker = {};
    for (const key of MARKER_TIMESTAMPS) {
      const value = raw[key];
      if (typeof value === "number" && Number.isFinite(value) && value >= 0) marker[key] = value;
    }
    if (raw.runtimePath === "legacy" || raw.runtimePath === "v3") marker.runtimePath = raw.runtimePath;
    return marker;
  } catch {
    return undefined;
  }
}

/**
 * The tracker is created lazily, on first use, and behind a try/catch: a throw
 * at module-evaluation time (MINOR-3) would take the whole bundle down before
 * bootstrap ever ran. If construction fails we fall back to a no-op tracker so
 * every call site below stays a plain one-liner.
 */
const NOOP_PAYLOAD_TRACKER: ClientPerformanceTracker = {
  startAttempt: () => "",
  mark: () => undefined,
  completeSuccess: () => undefined as unknown as ClientPerformanceAttemptPayload,
  completeFailure: () => undefined as unknown as ClientPerformanceAttemptPayload,
  completeNotApplicable: () => undefined as unknown as ClientPerformanceAttemptPayload,
};

let trackerInstance: ClientPerformanceTracker | undefined;
function tracker(): ClientPerformanceTracker {
  if (!trackerInstance) {
    try {
      trackerInstance = createClientPerformanceTracker({ marker: readMarker() });
    } catch {
      trackerInstance = NOOP_PAYLOAD_TRACKER;
    }
  }
  return trackerInstance;
}

let reported = false;
let visibleMarked = false;

/**
 * Why an attempt was latched without being reported. Client-side only — none of
 * these ever reach the server, so no result enum changes.
 */
/**
 * WHICH network call failed. `configuration_request_failed` is raised from three
 * different lookups and, until 2026-09-11, the report could not tell them apart:
 * 173 of 174 carried the bare reason `TypeError`, which is just what `fetch()`
 * rejects with, from any of them, for any network-level reason. Knowing the
 * phase is the difference between "something threw somewhere" and "the pageurl
 * lookup is failing for 18% of shoppers".
 *
 * Only a phase that can actually raise this result is listed. `pageurl_lookup`
 * was the other one until DIV-022 made a rejected pageurl lookup a non-event;
 * bundles cached in shoppers' browsers keep sending it for a while. The
 * product lookup has no catch of its own — it falls through to the outer
 * handler as `runtime_error` — so there is no third literal to guess at.
 *
 * A fixed set of literals, never free text — it is appended to the failure
 * reason, which must stay safe to store (no URLs, no messages, no stacks).
 */
export type ClientPerformancePhase = "calculator_app";

export type ClientPerformanceSuppressedReason =
  | "page_hidden"
  | "unloading"
  | "offline"
  | "no_calculator_assigned";

/**
 * Set as soon as the page starts going away. A `pagehide`/`beforeunload` means
 * the document is unloading; a `visibilitychange` to hidden means the shopper
 * switched away (bfcache on iOS, tab switch, app backgrounded). Either way an
 * in-flight fetch is about to be aborted by the browser and the resulting
 * TypeError says nothing about our API.
 */
let pageLeavingReason: "page_hidden" | "unloading" | undefined;

/**
 * Registered ONCE at module evaluation, fully guarded: these listeners must be
 * in place before the pageurl lookup starts (it is the first thing DetectPage
 * does), and a throw here would take the whole bundle down (MINOR-3). All
 * passive and never removed — they only flip a boolean.
 */
(function installPageLeavingListeners(): void {
  try {
    if (typeof window === "undefined" || typeof window.addEventListener !== "function") return;
    const opts = { capture: true, passive: true } as AddEventListenerOptions;
    window.addEventListener("pagehide", () => { pageLeavingReason = "unloading"; }, opts);
    window.addEventListener("beforeunload", () => { pageLeavingReason = "unloading"; }, opts);
    window.addEventListener(
      "visibilitychange",
      () => {
        if (isDocumentHidden()) pageLeavingReason = pageLeavingReason ?? "page_hidden";
      },
      opts,
    );
    // Safari fires visibilitychange on `document` only in some versions.
    if (typeof document !== "undefined" && typeof document.addEventListener === "function") {
      document.addEventListener(
        "visibilitychange",
        () => {
          if (isDocumentHidden()) pageLeavingReason = pageLeavingReason ?? "page_hidden";
        },
        opts,
      );
    }
  } catch {
    // telemetry must never throw into the bundle
  }
})();

function isDocumentHidden(): boolean {
  try {
    return typeof document !== "undefined" && document.visibilityState === "hidden";
  } catch {
    return false;
  }
}

function isOffline(): boolean {
  try {
    return typeof navigator !== "undefined" && navigator.onLine === false;
  } catch {
    return false;
  }
}

/**
 * A `configuration_request_failed` is only worth a row when the shopper was
 * actually there to see it fail. Anything else reports unconditionally.
 */
function benignFailureReason(
  result: ClientPerformanceFailureResult,
): ClientPerformanceSuppressedReason | undefined {
  if (result !== "configuration_request_failed") return undefined;
  if (pageLeavingReason) return pageLeavingReason;
  if (isDocumentHidden()) return "page_hidden";
  if (isOffline()) return "offline";
  return undefined;
}

/**
 * A short, safe failure code. NEVER the message text or the stack — those can
 * carry merchant/customer data (error messages routinely embed URLs).
 */
function safeFailureReason(reason: unknown, phase?: ClientPerformancePhase): string | undefined {
  const base = safeErrorName(reason);
  if (base === undefined) return phase;
  return phase === undefined ? base : `${base}:${phase}`;
}

function safeErrorName(reason: unknown): string | undefined {
  try {
    if (reason === undefined || reason === null) return undefined;
    const raw =
      typeof reason === "string"
        ? reason
        : (reason as { name?: unknown }).name ??
          (reason as { constructor?: { name?: unknown } }).constructor?.name;
    if (typeof raw !== "string" || raw === "") return "unknown_error";
    const safe = raw.replace(/[^A-Za-z0-9_]/g, "").slice(0, 64);
    return safe === "" ? "unknown_error" : safe;
  } catch {
    return "unknown_error";
  }
}

/**
 * Identity fields arrive from legacy bootstrap as `any` (a calculator id can be
 * a number on some code paths). Coerce to string at this boundary so a numeric
 * id can never 400 the DTO (MINOR-5); drop null/undefined entirely.
 */
function normalizeIdentity(identity?: Identity): Identity | undefined {
  if (!identity) return undefined;
  const normalized: Identity = {};
  const copy = (key: keyof Identity) => {
    const value = (identity as Record<string, unknown>)[key];
    if (value === undefined || value === null || value === "") return;
    normalized[key] = String(value);
  };
  copy("calculatorId");
  copy("calculatorName");
  copy("calculatorVersion");
  return normalized;
}

/**
 * Local, non-blocking delivery diagnostic (spec §Delivery). Exposed as a
 * NON-ENUMERABLE `window.cpc.clientPerformance` — same shape as `window.cpc.$`
 * — so a failed report is observable in the console (and by the e2e suite)
 * without adding a key to the enumerated `window.cpc` surface.
 */
export interface ClientPerformanceDiagnostics {
  lastAttemptId?: string;
  /** `"suppressed"` means the attempt was latched deliberately, never sent. */
  lastOutcome?: ClientPerformanceDeliveryOutcome | "suppressed";
  /** Only set alongside `lastOutcome: "suppressed"`. Debug aid, never sent. */
  suppressedReason?: ClientPerformanceSuppressedReason;
  deliveryFailures: number;
}

const diagnostics: ClientPerformanceDiagnostics = { deliveryFailures: 0 };

function exposeDiagnostics(): void {
  try {
    window.cpc = window.cpc || {};
    if (window.cpc.clientPerformance !== diagnostics) {
      Object.defineProperty(window.cpc, "clientPerformance", {
        value: diagnostics,
        enumerable: false,
        configurable: true,
      });
    }
  } catch {
    // diagnostics are best-effort — never throw into bootstrap
  }
}

function recordOutcome(outcome: ClientPerformanceDiagnostics["lastOutcome"]): void {
  try {
    diagnostics.lastOutcome = outcome;
    if (outcome === "failed") diagnostics.deliveryFailures += 1;
    exposeDiagnostics();
  } catch {
    // diagnostics are best-effort — never throw into bootstrap
  }
}

function recordSuppressed(reason: ClientPerformanceSuppressedReason): void {
  try {
    diagnostics.suppressedReason = reason;
    recordOutcome("suppressed");
  } catch {
    // diagnostics are best-effort — never throw into bootstrap
  }
}

function send(payload: ClientPerformanceAttemptPayload | undefined): void {
  if (!payload) return; // no-op tracker fallback produced nothing to report
  const shop = appState.cpc_shopUrl;
  const baseApiUrl = appState.baseCalculatorNewApiUrl;
  if (!shop || !baseApiUrl) return; // nothing to attribute the attempt to — stay silent
  // DIV-037: whether this view rendered from the cached payload, and whether the
  // revalidation had already proven it stale by the time the attempt ended.
  try {
    payload.payloadSource = payloadSource();
  } catch {
    // never let a diagnostic break delivery
  }
  try {
    diagnostics.lastAttemptId = payload.attemptId;
    exposeDiagnostics();
  } catch {
    // diagnostics are best-effort
  }
  // Still fire-and-forget: never awaited, and the diagnostic update itself is
  // guarded so a hostile window.cpc cannot break delivery.
  reportClientPerformanceAttempt(payload, shop, resolveClientPerformanceEndpoint(baseApiUrl))
    .then((outcome) => recordOutcome(outcome))
    .catch(() => recordOutcome("failed"));
}

/** Test/diagnostic aid: the live delivery diagnostic for this page attempt. */
export function getClientPerformanceDiagnostics(): ClientPerformanceDiagnostics {
  return diagnostics;
}

export function startClientPerformanceAttempt(identity?: Identity): void {
  try {
    tracker().startAttempt(normalizeIdentity(identity));
  } catch {
    // telemetry must never throw into bootstrap
  }
}

export function markClientPerformance(milestone: ClientPerformanceMilestone): void {
  try {
    tracker().mark(milestone);
  } catch {
    // telemetry must never throw into bootstrap
  }
}

/**
 * Marks `calculator_visible` only when the rendered calculator root is really
 * on screen: laid out with a non-zero box AND not display:none/visibility:hidden.
 * When the root exists but is zero-sized at this instant (a lazy theme section,
 * an ancestor still collapsed) the milestone is deliberately LEFT UNSET — the
 * attempt still completes successfully with `calculatorVisibleMs` undefined,
 * which is the honest signal, and never a guessed timestamp.
 */
export function markClientPerformanceVisible(): void {
  // MINOR-4: the milestone is latched, but `window.cpc.renderCalculator`
  // (preview) re-enters the same render path — skip the getBoundingClientRect/
  // getComputedStyle pair (a forced reflow) once it is already recorded.
  if (visibleMarked || reported) return;
  try {
    const root = document.getElementById("calculator");
    if (!root) return;
    const rect = root.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return;
    const style = window.getComputedStyle(root);
    if (style.display === "none" || style.visibility === "hidden") return;
    tracker().mark("calculator_visible");
    visibleMarked = true;
  } catch {
    // telemetry must never throw into bootstrap
  }
}

export function completeClientPerformanceSuccess(identity?: Identity): void {
  if (reported) return;
  reported = true;
  try {
    send(tracker().completeSuccess(normalizeIdentity(identity)));
  } catch {
    // telemetry must never throw into bootstrap
  }
}

/**
 * `reason` accepts either a ready-made short code or the caught error itself —
 * it is normalized to a safe code here so call sites stay one line.
 */
export function completeClientPerformanceFailure(
  result: ClientPerformanceFailureResult,
  reason?: unknown,
  identity?: Identity,
  phase?: ClientPerformancePhase,
): void {
  if (reported) return;
  reported = true;
  const benign = benignFailureReason(result);
  if (benign) {
    // Latched, not sent: the attempt is over (nothing may report later) but the
    // failure tells us nothing about the calculator, only about the shopper's
    // connection or attention.
    recordSuppressed(benign);
    return;
  }
  try {
    send(
      tracker().completeFailure(result, {
        ...normalizeIdentity(identity),
        failureReason: safeFailureReason(reason, phase),
      }),
    );
  } catch {
    // telemetry must never throw into bootstrap
  }
}

/**
 * The NOT-APPLICABLE terminal: the calculator lookup answered, and the answer
 * was "there is no calculator for this page". Reported, not suppressed —
 * the API keeps it out of every rate, and it is the only way to tell a store
 * whose calculators are assigned to no products apart from a store that has no
 * calculators at all.
 *
 * NOT SENT TO THE SERVER as of 2026-09-11. It was the single largest source of
 * ingest traffic — 2,104,507 rows in 24 hours against 164,936 attempts that
 * measured anything — and it never said more than "this store has a calculator
 * assigned to no products", which is a fact we already own in the database and
 * can read without waiting for a shopper to visit. Reading it there also covers
 * stores with no traffic at all, which telemetry never could.
 *
 * The attempt is still LATCHED, so a later complete* on the same page view
 * cannot report in its place, and the reason is still visible in
 * `window.cpc.clientPerformance` for debugging a specific store.
 *
 * The server still accepts and counts this result: merchant browsers cache
 * calculator.js, so bundles that predate this change keep sending it for days.
 * The ingest path is unchanged — only this bundle stops.
 */
export function completeClientPerformanceNoCalculatorAssigned(_identity?: Identity): void {
  if (reported) return;
  reported = true;
  recordSuppressed("no_calculator_assigned");
}

/** Test/diagnostic aid: has this page already sent its one report? */
export function hasReportedClientPerformance(): boolean {
  return reported;
}
